/**
 * Currency Context - Multi-currency support
 * Supports JMD, USD, CAD, GBP
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CURRENCIES = {
  JMD: { name: 'Jamaican Dollar', symbol: 'J$', flag: '🇯🇲' },
  USD: { name: 'US Dollar', symbol: '$', flag: '🇺🇸' },
  CAD: { name: 'Canadian Dollar', symbol: 'C$', flag: '🇨🇦' },
  GBP: { name: 'British Pound', symbol: '£', flag: '🇬🇧' },
};

const DEFAULT_RATES = {
  JMD: 1.0,
  USD: 0.0064,
  CAD: 0.0087,
  GBP: 0.0051,
};

const CurrencyContext = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrencyState] = useState(() => {
    return localStorage.getItem('liantiao_currency') || 'JMD';
  });
  const [rates, setRates] = useState(DEFAULT_RATES);
  const [loading, setLoading] = useState(true);

  // Fetch exchange rates on mount
  useEffect(() => {
    const fetchRates = async () => {
      try {
        const res = await fetch(`${API_URL}/api/currency/rates`);
        const data = await res.json();
        if (data.rates) {
          setRates(data.rates);
        }
      } catch (error) {
        console.error('Failed to fetch exchange rates:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRates();
  }, []);

  const setCurrency = useCallback((newCurrency) => {
    if (CURRENCIES[newCurrency]) {
      setCurrencyState(newCurrency);
      localStorage.setItem('liantiao_currency', newCurrency);
    }
  }, []);

  const convert = useCallback((amount, fromCurrency = 'JMD', toCurrency = currency) => {
    if (fromCurrency === toCurrency) return amount;
    
    // Convert to JMD first
    let jmdAmount = amount;
    if (fromCurrency !== 'JMD') {
      const fromRate = rates[fromCurrency] || 1;
      jmdAmount = amount / fromRate;
    }
    
    // Convert from JMD to target
    const toRate = rates[toCurrency] || 1;
    return Math.round(jmdAmount * toRate * 100) / 100;
  }, [currency, rates]);

  const formatPrice = useCallback((amount, currencyCode = currency) => {
    const info = CURRENCIES[currencyCode] || CURRENCIES.JMD;
    const converted = currencyCode !== 'JMD' ? convert(amount, 'JMD', currencyCode) : amount;
    return `${info.symbol}${converted.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, [currency, convert]);

  const getCurrencyInfo = useCallback((code = currency) => {
    return CURRENCIES[code] || CURRENCIES.JMD;
  }, [currency]);

  const value = {
    currency,
    setCurrency,
    currencies: CURRENCIES,
    rates,
    loading,
    convert,
    formatPrice,
    getCurrencyInfo,
    symbol: CURRENCIES[currency]?.symbol || 'J$',
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return context;
};

export default CurrencyContext;
